/**
 * The questionnaire palette, in one place.
 *
 * These are literal hex values rather than `gray-*` tokens because the 2026
 * design language sits in a band the shared scale does not cover: the body grey
 * `#878787` falls between `gray-400` (#A3A3A3) and `gray-500` (#737373), and
 * snapping to either is visible in running text. The auth modal reached the
 * same conclusion and hard-codes the same values.
 *
 * They are collected here, rather than spread across the components, so the
 * palette is greppable and a later move onto tokens is one file.
 *
 * The strings must stay literal: Tailwind scans source text, so a value
 * assembled at runtime produces no CSS.
 */
export const QUESTIONNAIRE_COLOUR = {
    /** Answer-card and inactive-surface fill. */
    surface: 'bg-[#f6f6f6]',
    /** Answer-card fill while the pointer is over it. */
    surfaceHover: 'hover:bg-[#efefef]',
    /** Selected answer card: inverted, matching the Continue pill. */
    surfaceSelected: 'bg-[#181818]',
    /** Primary heading and answer-card titles. */
    ink: 'text-[#161616]',
    /** The large step heading, a touch lighter than pure ink. */
    inkHeading: 'text-[#3d3d3d]',
    /** Supporting copy: subheadings, helper text, footer legal. */
    inkMuted: 'text-[#878787]',
    /** Answer-card descriptions, one step darker than `inkMuted`. */
    inkSubtle: 'text-[#666666]',
    /** The uppercase micro-label above the answer column. */
    inkLabel: 'text-[#8c8c8c]',
    /** Continue pill. */
    actionPrimary: 'bg-[#333333]',
    /** Back pill outline. */
    actionOutline: 'border-[#5f5f5f]',
    /** Back pill label. */
    actionOutlineInk: 'text-[#181818]',
    /** Footer rule and control hairlines. */
    hairline: 'border-[#eaeaea]',
} as const;

/**
 * Step-to-step motion, shared with the two-pane modal so the two surfaces
 * settle at the same speed.
 *
 * The incoming step catches up over the front of the transition rather than
 * appearing whole, which is what stops a step change reading as a jump.
 */
export const STEP_SETTLE_MS = 260;

/** Matches the two-pane sheet's carry-in curve. */
export const STEP_SETTLE_EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';

/**
 * Answer-column widths, from the design.
 *
 * `narrow` carries stacked cards and single inputs; `wide` carries the style
 * grid and the brief textarea. Sizes use the numeric scale (design-px / 4) so
 * the same class renders correctly in Business Manager, whose root font size
 * differs from the website's.
 *
 * The cap is held back until `md`. The website's root font size is fluid, so a
 * rem-based maximum shrinks along with the viewport — on a phone the 469px
 * column measures nearer 250px and leaves a gutter the design never had. There
 * is nothing beside the answers at that width for a cap to protect them from.
 */
export const QUESTIONNAIRE_COLUMN_WIDTH = {
    narrow: 'w-full md:max-w-117.25',
    wide: 'w-full md:max-w-185',
    /**
     * No cap at all, for a step whose content is a grid that wants the whole
     * width left of the pane — the membership checkout's confirmation runs a
     * four-up card row that a 740px column would reflow to two.
     */
    full: 'w-full',
} as const;

export type QuestionnaireColumnWidth = keyof typeof QUESTIONNAIRE_COLUMN_WIDTH;
